/**
 * Reading a thrown request into the three things a failure toast can honestly
 * say: what the server answered with, what it said, and how to find the request
 * again when it said nothing useful.
 *
 * There are exactly three shapes to read, and the copy below is built so that
 * every one of them produces a true sentence:
 *
 * 1. `ApiRequestError` — the transport's typed non-2xx. Carries `status`, the
 *    server's message, and (since `@atsready/shared` v2.0.17) the response's
 *    `X-Request-Id`. On production the message is always `Internal server
 *    error` (`api/src/app/index.ts:428-430` redacts the real cause), so the
 *    reference is the only handle an operator has on the Worker log.
 * 2. A plain `Error` — a non-subscription 403 throws one
 *    (`shared/src/utils/api-client.ts:273`), and a plain `Error` has nowhere to
 *    put a status or an id. That is deliberate and stays: promoting it would
 *    change what every `catch` in app, admin and chrome-extension receives.
 * 3. Anything else thrown. There is no message to show at all.
 */

import { ApiRequestError } from '@atsready/shared/utils';

export interface ApiFailure {
  /** The HTTP status, when the transport kept one. `null` on a 403 and on a non-`Error` throw. */
  status: number | null;
  /** The server's sentence, when there was one. */
  message: string | null;
  /** The API's `X-Request-Id` for the failed response, when the transport could read one. */
  requestId: string | null;
}

/** M10's degraded reference line, for the failure that cannot carry an id. */
export const NO_REQUEST_REFERENCE =
  'No request reference — the transport dropped the status and headers.';

/** The no-message fallback, so the toast is never a bare status. */
export const NO_MESSAGE_SENTENCE = 'The request failed.';

export function describeApiFailure(error: unknown): ApiFailure {
  if (error instanceof ApiRequestError) {
    return {
      status: error.status,
      message: error.message === '' ? null : error.message,
      requestId: error.requestId ?? null,
    };
  }

  if (error instanceof Error) {
    return {
      status: null,
      message: error.message === '' ? null : error.message,
      requestId: null,
    };
  }

  return { status: null, message: null, requestId: null };
}

/**
 * `HTTP 500 — Internal server error`, or just the sentence when the status was
 * lost, or the fallback when there was no sentence either.
 */
export function failureLead(failure: ApiFailure): string {
  const prefix = failure.status === null ? '' : `HTTP ${failure.status} — `;
  return `${prefix}${failure.message ?? NO_MESSAGE_SENTENCE}`;
}

/** `Reference {id}`, or the sentence that says why there is not one. */
export function failureReference(failure: ApiFailure): string {
  return failure.requestId === null
    ? NO_REQUEST_REFERENCE
    : `Reference ${failure.requestId}`;
}
